// src/context/AuthContext.jsx
import { createContext, useEffect, useRef, useState } from "react";
import api from "../api/axios";

export const AuthContext = createContext();

// decode exp from JWT (no deps)
const getTokenExpiration = (token) => {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" ? payload.exp : null; // seconds
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const token = localStorage.getItem("token");
    // if no token, clear any stored user and return null
    if (!token) {
      localStorage.removeItem("user");
      return null;
    }
    // if token expired, remove both and return null
    const exp = getTokenExpiration(token);
    if (!exp || exp * 1000 < Date.now()) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      return null;
    }
    // otherwise restore user if available
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const [completeProfile, setCompleteProfile] = useState(null);
  const [successfulCreation, setSuccessfulCreation] = useState(null);

  const logoutTimerRef = useRef(null);

  const clearTimers = () => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      clearInterval(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  };

  const logout = () => {
    // clear token AND user immediately
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    // clear react state
    setUser(null);
    setCompleteProfile(null);
    setSuccessfulCreation(null);

    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      clearInterval(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }

    // do NOT navigate here — let App handle client-side navigation on auth:logout
  };

  const scheduleLogoutAt = (expInSeconds) => {
    clearTimers();

    const msUntil = expInSeconds * 1000 - Date.now();
    if (msUntil <= 0) {
      logout();
      return;
    }

    const MAX_TIMEOUT = 2147483647; // ~24.8 days
    if (msUntil > MAX_TIMEOUT) {
      // periodic check
      logoutTimerRef.current = setInterval(() => {
        const token = localStorage.getItem("token");
        const exp = token && getTokenExpiration(token);
        if (!exp || exp * 1000 < Date.now()) {
          logout();
        }
      }, 60 * 1000);
    } else {
      logoutTimerRef.current = setTimeout(() => {
        logout();
      }, msUntil);
    }
  };

  const fetchProfile = async () => {
    try {
      const { data: profile } = await api.get("/users/me");
      setUser(profile);

      const isComplete =
        Boolean(profile.first_name) &&
        Boolean(profile.last_name) &&
        Boolean(profile.country) &&
        Boolean(profile.city) &&
        Boolean(profile.phone_number) &&
        Boolean(profile.date_of_birth);

      setCompleteProfile(isComplete);
      return true;
    } catch (err) {
      console.error("Fetch profile error:", err.response?.data || err.message);
      // ensure local cleanup
      localStorage.removeItem("token");
      setUser(null);
      setCompleteProfile(null);
      return false;
    }
  };

  useEffect(() => {
    // keep localStorage.user in sync with state
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.removeItem("user");
    }
  }, [user]);

  useEffect(() => {
    // on mount: if token exists, validate expiry, schedule logout, and try to fetch profile
    const token = localStorage.getItem("token");
    if (!token) return;

    const exp = getTokenExpiration(token);
    if (!exp || exp * 1000 < Date.now()) {
      logout();
      return;
    }

    scheduleLogoutAt(exp);
    fetchProfile();

    // Listen for api-broadcasted logout events (axios interceptor dispatches these)
    const onAuthLogout = () => {
      // clear user but don't redirect; App will navigate
      logout();
    };
    window.addEventListener("auth:logout", onAuthLogout);

    // Listen to storage events (other tabs may remove token)
    const onStorage = (e) => {
      if (e.key === "token" && e.newValue === null) {
        logout();
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("auth:logout", onAuthLogout);
      window.removeEventListener("storage", onStorage);
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    const token = data.token;
    localStorage.setItem("token", token);

    const exp = getTokenExpiration(token);
    if (exp) scheduleLogoutAt(exp);

    await fetchProfile();
  };

 const register = async (name, email, password) => {
  try {
    // Just create the account - don't store token or fetch profile
    await api.post("/auth/register", {
      name,
      email,
      password,
    });
    
    setSuccessfulCreation(true);
    return true;
  } catch (err) {
    console.error("Registration error:", err.response?.data || err.message);
    setSuccessfulCreation(false);
    return false;
  }
};

  const resetSuccessfulCreation = () => {
    setSuccessfulCreation(null);
  };

  const completeProfileFunc = async (
    first_name,
    last_name,
    profile_picture,
    country,
    city,
    phone_number,
    birthDate
  ) => {
    try {
      const token = localStorage.getItem("token");
      await api.put(
        "/users/me/completeProfile",
        {
          first_name,
          last_name,
          profile_picture,
          country,
          city,
          phone_number,
          birthDate,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      await fetchProfile();
      return true;
    } catch (err) {
      console.error("Complete profile error:", err.response?.data || err.message);
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        completeProfile,
        successfulCreation,
        login,
        register,
        fetchProfile,
        completeProfileFunc,
        logout,
        resetSuccessfulCreation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
